import { z } from 'zod';

export function fixGoDate(date: Date, dateString: string | null | undefined): Date | null {
  if (dateString == null || dateString.startsWith('0001')) {
    return null;
  }

  return date;
}

export function fixGoSlice<T>(slice: T[] | null): T[] {
  if (slice === null) {
    return [];
  }

  return slice;
}

// Zod ports of the fixups above, for schemas migrated off Yup.

// Which date helper a field wants is decided by its Go tag, not by whether the
// field "feels" optional. A time.Time is a value, so an unset one still
// marshals — as "0001-01-01T00:00:00Z", which parses into a perfectly valid
// year-1 Date that no reader is expecting:
//
//   *time.Time                       null / absent  → zGoNullableDate
//   time.Time  `json:"x,omitzero"`   absent         → zGoNullableDate
//   time.Time  `json:"x"`            "0001-01-..."  → zGoNullableDate
//                                                     unless the field is
//                                                     structurally always
//                                                     written (created_at,
//                                                     updated_at), which is
//                                                     the only case for zGoDate
//
// A bare `time.Time` reaching the app as a year-1 Date is essentially never
// what the reader wants: `value || fallback` stops falling back, date-diff
// staleness checks return two millennia, and formatters print "Jan 1, 1".
// Prefer zGoNullableDate whenever the zero time is reachable at all.

// ISO date string → Date, for a Go timestamp that is always populated. Only
// strings are coerced (z.coerce.date would turn null into the 1970 epoch); Date
// instances pass through, everything else is rejected by z.date(). It does NOT
// special-case the zero time — reach for zGoNullableDate if that's reachable.
export const zGoDate = z.preprocess(
  (value) => (typeof value === 'string' ? new Date(value) : value),
  z.date()
);

// Nullable Go date: Go marshals the zero time.Time as "0001-01-01T00:00:00Z";
// treat it — and null/absent — as null, like fixGoDate.
export const zGoNullableDate = z.preprocess(
  (value) =>
    value == null || (typeof value === 'string' && value.startsWith('0001')) ? null : value,
  z.preprocess(
    (value) => (typeof value === 'string' ? new Date(value) : value),
    z.date().nullable()
  )
);

// Go marshals a nil slice as null; treat it as the empty array, like fixGoSlice.
export function zGoSlice<T extends z.ZodType>(element: T) {
  return z.preprocess((value) => value ?? [], z.array(element));
}
